import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './CadastroEquipe.module.css'; // Reutilizando o CSS de Cadastro
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import CustomAlert from '../../components/CustomAlert/CustomAlert'; // Adicionado
import axios from 'axios';
import { FiCheck, FiTruck, FiAlertCircle, FiEdit, FiSave } from 'react-icons/fi'; // Adicionado FiSave para o botão
import { API_BASE_URL } from '../../config';

const EditarEquipe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nomeEquipe, setNomeEquipe] = useState('');
  const [veiculos, setVeiculos] = useState([]); // Todos os veiculos disponíveis
  const [selectedVeiculos, setSelectedVeiculos] = useState([]); // IDs dos veiculos da equipe
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [equipeOriginalName, setEquipeOriginalName] = useState('');

  // NOVO ESTADO: CustomAlert (Faltava aqui!)
  const [alert, setAlert] = useState({
      show: false,
      type: 'info',
      title: '',
      text: '',
      showCancelButton: false,
      onConfirm: null,
      onCancel: () => setAlert(prev => ({ ...prev, show: false })), // Função padrão para fechar
      autoClose: 0,
  });
  
  // NOVA FUNÇÃO: Disparar o alerta (Faltava aqui!)
  const showAlert = useCallback((options) => {
      setAlert({
          show: true,
          type: options.icon,
          title: options.title,
          text: options.text,
          showCancelButton: options.showCancelButton || false,
          onConfirm: options.confirmAction || options.cancelAction || (() => setAlert(prev => ({ ...prev, show: false }))),
          onCancel: options.cancelAction || (() => setAlert(prev => ({ ...prev, show: false }))),
          autoClose: options.autoClose || 0,
      });
  }, []);

  const toggleVeiculo = useCallback((veiculoId) => {
    setSelectedVeiculos(prevSelected =>
      prevSelected.includes(veiculoId)
        ? prevSelected.filter(vId => vId !== veiculoId)
        : [...prevSelected, veiculoId]
    );
  }, []);

  // Função para buscar dados da equipe e todos os veículos disponíveis
  useEffect(() => {
    const fetchEquipeData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Usuário não autenticado.");

        // 1. Buscar todos os veículos disponíveis
        const veiculosResponse = await axios.get(`${API_BASE_URL}/api/veiculos`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        // 2. Buscar os detalhes da equipe (inclui veículos atualmente nela)
        const equipeResponse = await axios.get(`${API_BASE_URL}/api/colheita/equipes/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const equipeData = equipeResponse.data;
        const veiculosAtuais = equipeData.veiculos.map(v => v.id);

        setVeiculos(veiculosResponse.data);
        setNomeEquipe(equipeData.nome);
        setEquipeOriginalName(equipeData.nome);
        setSelectedVeiculos(veiculosAtuais);

      } catch (error) {
        console.error("Erro ao carregar dados da equipe:", error);
        
        const errorMsg = `Erro ao carregar dados da equipe: ${error.message || 'Erro desconhecido'}`;
        setError(errorMsg); // Mantém o erro no estado para debug
        
        // Uso do CustomAlert para erros de carregamento
        showAlert({
            icon: 'error',
            title: 'Falha no Carregamento',
            text: errorMsg,
            confirmAction: () => navigate('/lista-equipes') // Volta para a lista
        });

      } finally {
        setLoading(false);
      }
    };

    fetchEquipeData();
  }, [id, showAlert, navigate]); // Adicionado showAlert e navigate às dependências

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CORREÇÃO: Usar showAlert e RETURN para validação de nome
    if (!nomeEquipe.trim()) {
        showAlert({
            icon: 'warning',
            title: 'Atenção',
            text: 'O nome da equipe não pode ser vazio.',
        });
        return;
    }
    
    // CORREÇÃO: Usar showAlert e RETURN para validação de veículos
    if (selectedVeiculos.length === 0) {
        showAlert({
            icon: 'warning',
            title: 'Atenção',
            text: 'Selecione pelo menos um veículo para a equipe.',
        });
        return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Usuário não autenticado.");

      await axios.put(`${API_BASE_URL}/api/colheita/equipes/${id}`, {
        nome: nomeEquipe.trim(),
        veiculosIds: selectedVeiculos,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // CORREÇÃO: Uso do CustomAlert de Sucesso e redirecionamento
      showAlert({
          icon: 'success',
          title: 'Sucesso!',
          text: `A equipe "${nomeEquipe}" foi atualizada com sucesso!`,
          autoClose: 1500,
          // AÇÃO: Redireciona para a página de detalhes da equipe editada
          confirmAction: () => navigate(`/lista-equipes/${id}`) 
      });

    } catch (error) {
      console.error("Erro ao editar equipe:", error);
      const errorMsg = `Erro ao editar equipe: ${error.response?.data?.error || 'Erro desconhecido'}`;
      setError(errorMsg); // Mantém o erro no estado para debug

      // CORREÇÃO: Uso do CustomAlert de Erro
      showAlert({
          icon: 'error',
          title: 'Falha na Edição',
          text: errorMsg,
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingOverlay text="Carregando dados para edição..." />;
  }
  
  // CORREÇÃO: Removida a lógica antiga de renderização de erro

  const submitButtonText = isSubmitting ? 'Salvando...' : 'Salvar Alterações';

  return (
    <div className={styles.container}>
      {isSubmitting && <LoadingOverlay text="Salvando alterações..." />}
      
      <PageHeader
        title={`Editar Equipe: ${equipeOriginalName}`}
        subtitle={`Atualize o nome e os veículos associados à equipe #${id}`}
        backTo={`/lista-equipes/${id}`}
      />

      <div className={styles.formContainer}>
        
        {/* Não usamos mais a div de erro aqui, o CustomAlert fará o trabalho */}

        <div className={styles.inputGroup}>
          <label htmlFor="nomeEquipe">Nome da Equipe</label>
          <input
            id="nomeEquipe"
            type="text"
            value={nomeEquipe}
            onChange={(e) => setNomeEquipe(e.target.value)}
            placeholder="Ex: Equipe de Colheita Sul"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.sectionTitle}>
            <FiTruck /> Selecionar Veículos e Maquinários
        </div>

        {veiculos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                <p>Nenhum veículo disponível para seleção.</p>
            </div>
        ) : (
            <div className={styles.gridVeiculos}>
                {veiculos.map(veiculo => (
                    <div 
                        key={veiculo.id} 
                        className={`${styles.cardVeiculo} ${selectedVeiculos.includes(veiculo.id) ? styles.selected : ''}`}
                        onClick={() => !isSubmitting && toggleVeiculo(veiculo.id)}
                    >
                        <div className={styles.cardHeader}>
                            <span className={styles.tipo}>{veiculo.tipoVeiculo}</span>
                            {selectedVeiculos.includes(veiculo.id) && <FiCheck className={styles.checkIcon} />}
                        </div>
                        <h3>{veiculo.modelo}</h3>
                        <p>{veiculo.placa || veiculo.chassi || 'S/ Placa'}</p>
                        <span className={styles.marca}>{veiculo.marca}</span>
                    </div>
                ))}
            </div>
        )}

        <div className={styles.footerActions}>
            <button 
                className={styles.btnCancel} 
                onClick={() => navigate(`/lista-equipes/${id}`)}
                disabled={isSubmitting}
            >
                Cancelar
            </button>
            <button 
                className={styles.btnSave} 
                onClick={handleSubmit}
                disabled={isSubmitting || !nomeEquipe.trim()}
            >
                <FiSave style={{ marginRight: '0.5rem' }} /> {submitButtonText} {/* Trocado FiEdit por FiSave */}
            </button>
        </div>
      </div>
      
      {/* RENDERIZAÇÃO DO CUSTOM ALERT (Faltava aqui!) */}
      <CustomAlert 
          show={alert.show}
          type={alert.type}
          title={alert.title}
          text={alert.text}
          showCancelButton={alert.showCancelButton}
          onConfirm={alert.onConfirm}
          onCancel={alert.onCancel}
          autoClose={alert.autoClose}
      />
    </div>
  );
};

export default EditarEquipe;